import Router from 'next/router';
import { useState } from 'react';

import { useTrackRemovedUserFromWorkspace } from '@air/analytics';
import { type WorkspaceMemberResponse } from '@air/api/types';
import { ConfirmationModal } from '@air/component-confirmation-modal';
import { useToasts } from '@air/provider-toast';
import { convertUnknownToError } from '@air/utils-error';

import { Routes } from '~/constants/routes';
import { useSwitchWorkspace } from '~/hooks/useSwitchWorkspace';
import { useAccountContext } from '~/providers/AccountProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useRemoveWorkspaceMembers } from '~/swr-hooks/members/useRemoveWorkspaceMembers';
import { useUserByAccountIdOrEmail } from '~/swr-hooks/members/useUserByAccountIdOrEmail';
import { useWorkspaces } from '~/swr-hooks/workspaces/useWorkspaces';

type DeactivateMemberModalProps = {
  member: Pick<WorkspaceMemberResponse, 'firstName' | 'lastName' | 'accountId' | 'email'>;
};

export const DeactivateMemberModal = ({
  member: { firstName, lastName, accountId, email },
  onClose,
}: AirModalProps<DeactivateMemberModalProps>) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { switchWorkspace } = useSwitchWorkspace();
  const { data: account } = useAccountContext();
  const [deactivating, setDeactivating] = useState(false);
  const { showToast } = useToasts();
  const { removeWorkspaceMembers } = useRemoveWorkspaceMembers();
  const { data: workspaces = [] } = useWorkspaces();
  const { user } = useUserByAccountIdOrEmail({ userId: accountId ?? email });
  const { trackRemovedUserFromWorkspace } = useTrackRemovedUserFromWorkspace();

  const trackingData = {
    removerId: account?.id,
    removedUserId: user?.accountId ?? '',
    removedUserRole: `${user?.type} ${user?.roles.member.displayName}`,
    wasBilled: user?.isBilled,
    wasInvitePending: user?.pending,
  };

  const onDeactivate = async () => {
    setDeactivating(true);

    const members = [accountId ? { accountId } : { email }];

    const workspaceId = currentWorkspace?.id;
    if (!workspaceId) {
      throw new Error('No workspace id');
    }

    // Part of a Temporary solution for sysadmins to remove themselves from workspace
    if (accountId === account?.authId) {
      if (workspaces.length < 2) {
        onClose();
        showToast(`Cannot deactivate a member from their only workspace`);
        return;
      }

      const fallBackWorkspace = workspaces.filter((workspace) => workspace.id !== workspaceId)[0];

      /**
       * Temporary fix: We'll need to revisit this flow on the
       * future + migrate this to useMutation.
       */
      try {
        await removeWorkspaceMembers({ workspaceId, members });
      } catch (error) {
        setDeactivating(false);
        showToast(convertUnknownToError(error).message, { color: 'red' });
      }

      trackRemovedUserFromWorkspace(trackingData);

      switchWorkspace({ id: fallBackWorkspace.id });
      Router.push(Routes.home);
      showToast(`${firstName && lastName ? `${firstName} ${lastName} was deactivated` : 'Member was deactivated'}`);

      return;
    }

    /**
     * Temporary fix: We'll need to revisit this flow on the
     * future + migrate this to useMutation.
     */
    try {
      await removeWorkspaceMembers({ workspaceId, members });
      trackRemovedUserFromWorkspace(trackingData);
      showToast(`${firstName && lastName ? `${firstName} ${lastName} was deactivated` : 'Member was deactivated'}`);
      onClose();
    } catch (error) {
      setDeactivating(false);
      showToast(convertUnknownToError(error).message, { color: 'red' });
    }
  };

  return (
    <ConfirmationModal
      id="deactivate-member-modal"
      isOpen
      title="Are you sure?"
      description={`Removing  ${
        firstName && lastName ? `${firstName} ${lastName}` : 'this member'
      } will prevent them from accessing the workspace. Any content they created will not be removed or deleted.`}
      onDismiss={onClose}
      ctaPrimaryText="Remove"
      isLoading={deactivating}
      isDisabled={deactivating}
      onConfirm={onDeactivate}
      isDangerous
    />
  );
};
